import { Alert, AlertProps, AlertTitle, AlertTitleProps, SxProps, Theme } from '@mui/material'
import { FC, ReactNode } from 'react'

export type C1AlertSeverityType = 'primary' | 'info' | 'error' | 'warning' | 'success'

export type C1AlertVariantType = 'outlined' | 'banner' | 'standard'

export type C1AlertTitleProps = AlertTitleProps

/**
 * @designSystem
 */
export type C1AlertProps = Omit<AlertProps, 'color' | 'variant' | 'severity'> & {
  /** Alert visual style. `outlined` (default) is a bordered alert; `banner` has a colored left bar for toasts and page banners. */
  variant?: C1AlertVariantType,
  /** Alert severity level. `primary` maps to `color=primary` with `severity=info` internally. */
  severity?: C1AlertSeverityType,
  /** Custom icon override. Set to a custom element to replace the default severity icon, or `false` to remove the icon entirely. */
  icon?: AlertProps['icon'],
  /** Action element rendered at the end of the alert (e.g. a button). Vertically centered alongside the message. */
  action?: ReactNode,
  /** Alert body content. Pair with `<C1AlertTitle>` for a title+description layout, or pass text directly for description-only. */
  children?: ReactNode,
  /** MUI system styles merged onto the root alert element. */
  sx?: SxProps<Theme>,
}

declare module '@mui/material/Alert' {
    export interface AlertPropsColorOverrides {
        primary: true
    }

    export interface AlertPropsVariantOverrides {
        banner: true
    }
}

export const C1AlertTitle: FC<C1AlertTitleProps> = (props) => <AlertTitle {...props} />

/**
 * Displays contextual feedback messages with severity levels.
 *
 * Wraps MUI Alert with a C1-specific `banner` variant and a custom `primary`
 * severity that maps to `color=primary` with `severity=info` internally.
 * Default icons use Lucide via C1Icon (Info, CircleCheck, TriangleAlert, CircleAlert).
 *
 * ## Variants
 * - `outlined` — default. Bordered alert with white background. Use for inline
 *   contextual messages within a page section.
 * - `banner` — white background with a colored 8px left bar. Used by the
 *   snackbar/toast system and page-level informational banners.
 *
 * ## Severities
 * - `info` — default. Neutral guidance, tips, general information.
 * - `success` — completed operations, confirmations, positive status.
 * - `warning` — destructive actions, approaching limits, things needing attention.
 * - `error` — failed operations, validation errors, system errors.
 * - `primary` — branded informational messages. Internally maps to
 *   `color=primary` with `severity=info`, so it uses the info icon by default.
 *
 * ## Behavior
 * - Use `<C1AlertTitle>` as the first child for a bold heading above the body text
 * - The `icon` prop accepts a custom element or `false` to remove it
 * - The `action` prop renders a button/element at the trailing end of the alert
 * - Icons render in a 30×30 rounded box; outlined variant adds a tinted background
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1Dialog | Use for blocking messages that require user action before continuing
 * @externalReference MUI Alert API | https://mui.com/material-ui/api/alert/
 */
export const C1Alert: FC<C1AlertProps> = ({ severity: _severity, icon, ...rest }) => {
    let severity: AlertProps['severity']
    let color: AlertProps['color']

    // MUI doesn't let you extend the 'severity' types. :(
    switch (_severity) {
        case 'primary':
            color = 'primary'
            severity = 'info'
            break
        default:
            color = _severity || 'info'
            severity = _severity || 'info'
            break
    }

    return <Alert
        color={color}
        severity={severity}
        icon={icon}
        {...rest}
    />
}
